import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { Observable, of, switchMap, map, tap, shareReplay } from 'rxjs';
import { RoleService } from './role.service';

export interface OrgUser {
  uid: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'banker' | 'reader' | 'collector';
  organizationId: string;
  organizationName: string;
  pin?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Current Firebase user (null when signed out) */
  readonly user$ = this.afAuth.authState;

  /** Resolved OrgUser with role and org context */
  readonly orgUser$: Observable<OrgUser | null>;

  private _orgUser: OrgUser | null = null;

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router,
    private roleService: RoleService,
  ) {
    this.orgUser$ = this.afAuth.authState.pipe(
      switchMap(user => {
        if (!user) return of(null);
        return this.afs.doc<any>(`OrganizationUsers/${user.uid}`).valueChanges().pipe(
          switchMap(orgUserDoc => {
            if (!orgUserDoc) return of(null);
            const orgId = orgUserDoc.OrganizationId || '';
            if (!orgId) return of(null);
            return this.afs.doc<any>(`Organizations/${orgId}`).valueChanges().pipe(
              map(orgDoc => ({
                uid: user.uid,
                email: user.email || '',
                displayName: user.displayName || orgUserDoc.displayName || orgUserDoc.Name || '',
                role: (orgUserDoc.Role || 'collector').toLowerCase() as OrgUser['role'],
                organizationId: orgId,
                organizationName: orgDoc?.Name || orgDoc?.name || '',
                pin: orgUserDoc.Pin || undefined,
              }))
            );
          })
        );
      }),
      tap(u => {
        this._orgUser = u;
        if (u) {
          this.roleService.setRole(u.role, u.uid);
        }
      }),
      shareReplay(1),
    );
  }

  get currentUser(): OrgUser | null { return this._orgUser; }

  get isAdmin(): boolean {
    return this._orgUser?.role === 'admin' || this._orgUser?.role === 'owner';
  }

  get isOwner(): boolean {
    return this._orgUser?.role === 'owner';
  }

  async login(email: string, password: string): Promise<void> {
    await this.afAuth.signInWithEmailAndPassword(email, password);
  }

  async logout(): Promise<void> {
    await this.afAuth.signOut();
    this.router.navigate(['/login']);
  }

  async sendPasswordReset(email: string): Promise<void> {
    await this.afAuth.sendPasswordResetEmail(email);
  }
}
